import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Checkbox, Flex, Typography } from 'antd';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getCategoryState } from '../store/categorySlice';
import {
    useDeleteProductCategoryMutation,
    useLazyReadCategoriesQuery,
    useUpdateProductCategoryMutation,
    useUpdateProductCategoryPositionMutation,
} from '../service/categories';
import { CategoryEditorModal } from '../components/CategoryEditorModal';
import { CategoriesTreeList } from '../components/CategoriesTreeList';
import { CategoryControls } from '../components/CategoryControls';
import { createFormData } from '../../../../../utils/common/createFormData';
import { useAppSelector } from '../../../../../store';
import { selectShopCurrent } from '../../shops/store/shopSlice';

export const CategoriesContainer = () => {
    const current = useAppSelector(selectShopCurrent);

    const { nestedCategoryList } = useSelector(getCategoryState);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [updatingCategoryId, setUpdatingCategoryId] = useState(null);
    const [openEditor, setOpenEditor] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [categoryName, setCategoryName] = useState('');
    const [currentCategory, setCurrentCategory] = useState();

    const [updatePositionCategory, { isLoading: isUpdateCategoryLoading }] =
        useUpdateProductCategoryPositionMutation();
    const [getCategories, { isLoading: isGetCategoriesLoading }] =
        useLazyReadCategoriesQuery();
    const [deleteCategory, { isLoading: isLoadingDelete }] =
        useDeleteProductCategoryMutation();
    const [updateCategory] = useUpdateProductCategoryMutation();

    useEffect(() => {
        if (current.id) {
            getCategories({ shop_id: current.id }, true);
        }
    }, [current.id]);

    const editableNestedCategoryList = useMemo(() => {
        return nestedCategoryList.filter(
            category => category.id !== null && category.id !== 0
        );
    }, [nestedCategoryList]);

    const onUpdatePosition = useCallback(
        async body => {
            setUpdatingCategoryId(body.category_id);
            try {
                await updatePositionCategory(body).unwrap();
                await getCategories({ shop_id: +current.id }).unwrap();
            } finally {
                setUpdatingCategoryId(null);
            }
        },
        [updatePositionCategory, getCategories, current.id]
    );

    const onSelectCategory = useCallback((categoryList: number[]) => {
        const [categoryId] = categoryList;
        setSelectedCategoryId(categoryId || null);
    }, []);

    const onDeleteCategory = useCallback(async () => {
        if (selectedCategoryId && typeof selectedCategoryId === 'number') {
            setUpdatingCategoryId(selectedCategoryId);
            try {
                await deleteCategory(selectedCategoryId).unwrap();
                setSelectedCategoryId(null);
            } finally {
                setUpdatingCategoryId(null);
            }
        }
    }, [selectedCategoryId, deleteCategory]);

    const onRenameCategory = category => {
        if (category) {
            setCurrentCategory(category);
            setCategoryName(category.name);
            setOpenEditor(true);
            setIsEdit(true);
        }
    };

    const onHighlight = useCallback(item => {
        const payload = {
            highlight: ['bold'],
        };

        const data = Object.fromEntries(
            Object.entries(item).filter(([_, value]) => value != null)
        );

        data.shop_id = current.id;

        if (data.description === '') {
            delete data.description;
        }
        if (!(data.image instanceof File)) {
            delete data.image;
        }

        updateCategory({
            id: Number(data.id),
            body: createFormData({ ...data, ...payload, _method: 'PUT' }),
        });
    }, []);

    return (
        <Fragment>
            <CategoryControls
                setIsEdit={setIsEdit}
                setOpenEditor={setOpenEditor}
                openEditor={openEditor}
                selectedCategoryId={selectedCategoryId}
            />

            <div>
                <Flex
                    style={{
                        padding: '16px 0',
                        background: '#25212905',
                    }}
                >
                    <StyledSelectorColumn>
                        {selectedCategoryId !== null && (
                            <Checkbox
                                indeterminate={true}
                                onChange={() => setSelectedCategoryId(null)}
                            />
                        )}
                    </StyledSelectorColumn>
                    <StyledTitleColumn>
                        <Typography.Text strong>
                            Название категории
                        </Typography.Text>
                    </StyledTitleColumn>
                    <StyledActionsColumn>
                        <Typography.Text strong>Действия</Typography.Text>
                    </StyledActionsColumn>
                </Flex>

                <CategoriesTreeList
                    categoryItems={editableNestedCategoryList}
                    updatingCategoryId={updatingCategoryId}
                    selectedCategoryId={selectedCategoryId}
                    isListUpdating={
                        isUpdateCategoryLoading ||
                        isGetCategoriesLoading ||
                        isLoadingDelete
                    }
                    onUpdatePosition={onUpdatePosition}
                    onSelectCategory={onSelectCategory}
                    onDeleteCategory={onDeleteCategory}
                    onRenameCategory={onRenameCategory}
                    onHighlight={onHighlight}
                />
            </div>

            <CategoryEditorModal
                currentCategory={currentCategory}
                open={openEditor}
                setOpen={setOpenEditor}
                categoryName={categoryName}
                categoryId={selectedCategoryId}
                setCategoryName={setCategoryName}
                isEdit={isEdit}
                setIsEdit={setIsEdit}
                parentId={selectedCategoryId}
            >
                <Typography.Text>
                    Внимание! Подкатегории можно перемещать в области своей
                    категории.
                </Typography.Text>
                {isEdit && (
                    <Typography.Text>
                        При наличии подключенного фида все изменения имен
                        категорий вернется к значениям в фиде.
                    </Typography.Text>
                )}
            </CategoryEditorModal>
        </Fragment>
    );
};

const StyledSelectorColumn = styled.div`
    flex: 0 0 44px;
`;

const StyledTitleColumn = styled.div`
    flex: 1 1 0px;
`;

const StyledActionsColumn = styled.div`
    flex: 0 0 252px;
`;
